import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { applyFiltersToParams, parsePageParam, setPageParam } from '@/utils';
import useDebouncedCallback from './useDebouncedCallback';

const useUrlFilters = <TFilters extends object>(parseFilters: (params: URLSearchParams) => TFilters) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseFilters(searchParams), [searchParams, parseFilters]);
  const page = parsePageParam(searchParams);

  const applyFilters = useCallback(
    (nextFilters: TFilters) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        setPageParam(params, 1);
        applyFiltersToParams(params, nextFilters);
        return params;
      });
    },
    [setSearchParams],
  );

  const debouncedApplyFilters = useDebouncedCallback(applyFilters, 500);

  const handleFilterChange = useCallback(
    <K extends keyof TFilters>(field: K, value: TFilters[K]) => {
      debouncedApplyFilters({ ...filters, [field]: value });
    },
    [debouncedApplyFilters, filters],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        setPageParam(params, nextPage);
        return params;
      });
    },
    [setSearchParams],
  );

  return { filters, page, handleFilterChange, handlePageChange };
};

export default useUrlFilters;
